import { addDays, milliseconds, startOfDay } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import memoizeOne from 'memoize-one';
import {
	type TimelineDuration,
	type TimelineMode,
	TimelineModeOptions,
} from '../../../../model/timeline';
import type { Percentage } from '../../../../types';
import { getTotalMilliseconds } from '../../../../utils/timeline';
import { DAY_VIEW_COLUMN_HOUR_INTERVAL } from '../constants';

const { DAYS, WEEKS, MONTHS, QUARTERS } = TimelineModeOptions;
// Size in pixels a column should ideally represent for any timeline mode
const WEEKS_COLUMN_WIDTH = 255;
// Size in pixels a single value should be for hours (36px for each hour)
const DAYS_COLUMN_WIDTH = 432;

/* Size in pixels a millisecond should ideally represent for a particular timeline mode
 * The averages days per month round to 30 on non leap-years
 * The average days per quarter round to 91 on non leap-years
 */
type TimelineModeBaselines = Record<TimelineMode, number>;

export const TIMELINE_MODE_BASELINES: TimelineModeBaselines = {
	[DAYS]: DAYS_COLUMN_WIDTH / milliseconds({ days: 1 }),
	[WEEKS]: WEEKS_COLUMN_WIDTH / milliseconds({ days: 7 }),
	[MONTHS]: WEEKS_COLUMN_WIDTH / milliseconds({ days: 30 }),
	[QUARTERS]: WEEKS_COLUMN_WIDTH / milliseconds({ days: 91 }),
};

// Given the totalDuration (milliseconds) of the timeline, calculate its width in pixels
export const getTimelineWidth = memoizeOne(
	(timelineMode: TimelineMode, duration: TimelineDuration): number =>
		getTotalMilliseconds(duration) * TIMELINE_MODE_BASELINES[timelineMode],
);

export type PercentPosition = {
	left: Percentage;
	right: Percentage;
};

const roundTwoDecimals = (n: number): number => Math.round(n * 100) / 100;
export const toPercentage = (decimal: number) => roundTwoDecimals(decimal * 100);

export const getRoundingDuration = memoizeOne((timelineMode: TimelineMode): number => {
	if (timelineMode === DAYS) {
		return milliseconds({ hours: DAY_VIEW_COLUMN_HOUR_INTERVAL });
	}
	if (timelineMode === WEEKS) {
		return milliseconds({ days: 1 });
	}
	throw Error('cannot find rounding duration');
});

/**
 * roundToMultiple rounds down to the nearest multiple using Math.floor
 * @param {*} num the number to round
 * @param {*} multiple round to this multiple
 */
export const roundToMultiple = (num: number, multiple: number) =>
	Math.floor(num / multiple) * multiple;

/**
 * getPositionFromDate accepts a Date object and calculate percentage position for the given time range(startMilliseconds ~ endMilliseconds)
 * uses utcOffset(0, true) to strip off timezone information preventing any daylight savings querks
 * left: percentage of the beginning of the given Date object date
 * right: percentage of the end of the given Date object date
 */
export const getPositionFromDateFn = memoizeOne(
	(startDate: Date, endDate: Date, timelineMode: TimelineMode) => {
		const start = zonedTimeToUtc(startOfDay(new Date(startDate)), 'utc').valueOf();
		const end = zonedTimeToUtc(startOfDay(addDays(new Date(endDate), 1)), 'utc').valueOf();
		const roundingDuration = getRoundingDuration(timelineMode); // either 1 day or X hours in milliseconds
		const duration = end - start;
		return (date: Date): PercentPosition => {
			const target = roundToMultiple(zonedTimeToUtc(date, 'utc').valueOf(), roundingDuration);
			return {
				left: toPercentage((target - start) / duration),
				right: toPercentage((end - (target + roundingDuration)) / duration),
			};
		};
	},
);
